import { type CSSProperties, type ReactNode } from 'react';
import type { Editor } from '@tiptap/core';

import { preventRichTextToolbarMouseDown } from './richTextToolbar';
import type { FontSizeValue } from './fontSizeExtension';

interface FontSizeOption {
  label: string;
  value: FontSizeValue | null;
}

const FONT_SIZE_OPTIONS: FontSizeOption[] = [
  { label: 'Default', value: null },
  { label: 'Large', value: '24px' },
  { label: 'Small', value: '16px' },
];

function getActiveFontSize(editor: Editor): FontSizeValue | null {
  const attrs = editor.getAttributes('textStyle') as Record<string, unknown>;
  const fontSize = attrs['fontSize'];
  if (fontSize === '24px' || fontSize === '16px') {
    return fontSize;
  }
  return null;
}

interface RichTextFontSizeDropdownProps {
  editor: Editor;
  selectStyle?: CSSProperties;
}

export function RichTextFontSizeDropdown({
  editor,
  selectStyle,
}: RichTextFontSizeDropdownProps): ReactNode {
  const activeFontSize = getActiveFontSize(editor);

  const defaultStyle: CSSProperties = {
    height: '32px',
    padding: '0 4px',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <select
      value={activeFontSize ?? ''}
      onChange={(e) => {
        const value = e.target.value as FontSizeValue | '';
        if (value === '') {
          editor.chain().focus().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
        } else {
          editor.chain().focus().setMark('textStyle', { fontSize: value }).run();
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ ...defaultStyle, ...selectStyle }}
      title="Font size"
      aria-label="Font size"
    >
      {FONT_SIZE_OPTIONS.map((option) => (
        <option key={option.label} value={option.value ?? ''}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
